'use client';
import React from 'react';
import AppImage from './ui/AppImage';

export interface IDCardData {
  displayName: string;
  detectiveAlias: string;
  consultantId: string;
  joinDate: string;
  currentRank: string;
  xpTotal: number;
  avatarUrl?: string;
  badges: string[];
}

interface DetectiveIDCardProps {
  data: IDCardData;
  compact?: boolean;
}

const BARCODE_HEIGHTS = [28,20,32,24,36,20,28,32,24,36,28,20,32,36,24,28,20,32,28,24,36,20,28,32,24,28,36,20,32,24];

function QRCodePlaceholder({ size = 56 }: { size?: number }) {
  const cells = [
    [1,1,1,1,1,1,1,0,1,0,1,1,1,1,1,1,1],
    [1,0,0,0,0,0,1,0,0,1,1,0,0,0,0,0,1],
    [1,0,1,1,1,0,1,0,1,0,1,0,1,1,1,0,1],
    [1,0,1,1,1,0,1,0,0,1,0,0,1,1,1,0,1],
    [1,0,1,1,1,0,1,0,1,1,1,0,1,1,1,0,1],
    [1,0,0,0,0,0,1,0,0,0,1,0,0,0,0,0,1],
    [1,1,1,1,1,1,1,0,1,0,1,1,1,1,1,1,1],
    [0,0,0,0,0,0,0,0,1,1,0,0,0,0,0,0,0],
    [1,1,0,1,1,0,1,1,0,1,1,0,1,0,1,1,0],
    [0,1,1,0,0,1,0,0,1,0,0,1,1,0,0,1,1],
    [1,0,1,1,0,0,1,1,0,1,0,0,1,1,0,0,1],
    [0,0,0,0,0,0,0,0,1,0,1,1,0,0,1,0,1],
    [1,1,1,1,1,1,1,0,0,1,1,0,1,0,0,1,0],
    [1,0,0,0,0,0,1,0,1,0,0,1,0,1,1,0,1],
    [1,0,1,1,1,0,1,0,0,1,1,0,1,0,1,1,0],
    [1,0,0,0,0,0,1,0,1,1,0,1,0,1,0,0,1],
    [1,1,1,1,1,1,1,0,0,0,1,0,1,1,0,1,0],
  ];
  const cellSize = size / 17;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
      <rect width={size} height={size} fill="white" />
      {cells.map((row, r) =>
        row.map((cell, c) =>
          cell ? (
            <rect
              key={`qr-${r}-${c}`}
              x={c * cellSize}
              y={r * cellSize}
              width={cellSize}
              height={cellSize}
              fill="#1a1a1a"
            />
          ) : null
        )
      )}
    </svg>
  );
}

function WatermarkSeal() {
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full opacity-10" fill="none">
      <circle cx="50" cy="50" r="46" stroke="#b8860b" strokeWidth="2.5" />
      <circle cx="50" cy="50" r="38" stroke="#b8860b" strokeWidth="1.5" />
      <circle cx="44" cy="44" r="14" stroke="#b8860b" strokeWidth="3" />
      <line x1="54" y1="54" x2="66" y2="66" stroke="#b8860b" strokeWidth="3.5" strokeLinecap="round" />
      {[0,45,90,135,180,225,270,315].map((deg, i) => {
        const rad = (deg * Math.PI) / 180;
        const x = 50 + 42 * Math.cos(rad);
        const y = 50 + 42 * Math.sin(rad);
        return <circle key={`star-${i}`} cx={x} cy={y} r="2" fill="#b8860b" />;
      })}
      <text x="50" y="78" textAnchor="middle" fontSize="6" fill="#b8860b" fontFamily="monospace" letterSpacing="1">OFFICIAL</text>
    </svg>
  );
}

export default function DetectiveIDCard({ data, compact = false }: DetectiveIDCardProps) {
  // Horizontal / landscape: exactly 3.5 wide × 2 tall ratio
  const cardW = compact ? 280 : 350;
  const cardH = compact ? 160 : 200;

  const issueDate = data.joinDate || '01/01/2026';
  const expYear = parseInt(issueDate.split('/')[2] || '2026') + 2;
  const expDate = issueDate.replace(/(\d+)$/, String(expYear));

  const photoW = compact ? 56 : 70;
  const photoH = compact ? 70 : 88;

  return (
    <div
      className="relative rounded-xl overflow-hidden shadow-2xl flex-shrink-0"
      style={{
        width: cardW,
        height: cardH,
        background: 'linear-gradient(135deg, #e8e8e8 0%, #f5f5f0 40%, #e0ddd5 100%)',
        border: '2px solid #c8b86a',
        fontFamily: 'monospace',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── GOLD HEADER BAR ── */}
      <div
        className="relative px-3 pt-2 pb-1.5 flex-shrink-0"
        style={{ background: 'linear-gradient(180deg, #d4a017 0%, #c8960c 50%, #b8860b 100%)' }}
      >
        {/* Top barcode row */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-end gap-px h-4">
            {BARCODE_HEIGHTS.slice(0, 15).map((h, i) => (
              <span
                key={`hbar-l-${i}`}
                style={{
                  display: 'inline-block',
                  width: i % 3 === 0 ? '2px' : '1px',
                  height: `${Math.round(h * 0.4)}px`,
                  background: 'rgba(0,0,0,0.7)',
                  borderRadius: '0.5px',
                }}
              />
            ))}
          </div>
          <div className="text-center">
            <h1
              className="font-extrabold tracking-[0.25em] text-white leading-none"
              style={{
                fontSize: compact ? '13px' : '16px',
                textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                letterSpacing: '0.3em',
              }}
            >
              P.S.Y.C.H.
            </h1>
            <p className="text-[7px] tracking-[0.1em] text-yellow-100/80 uppercase">
              Psychic Detective Agency · Santa Barbara
            </p>
          </div>
          <div className="flex items-end gap-px h-4">
            {BARCODE_HEIGHTS.slice(15).map((h, i) => (
              <span
                key={`hbar-r-${i}`}
                style={{
                  display: 'inline-block',
                  width: i % 3 === 0 ? '2px' : '1px',
                  height: `${Math.round(h * 0.4)}px`,
                  background: 'rgba(0,0,0,0.7)',
                  borderRadius: '0.5px',
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── CARD BODY: photo left, info right ── */}
      <div className="flex flex-1 relative overflow-hidden">
        {/* Watermark seal */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 0 }}>
          <div style={{ width: compact ? 90 : 110, height: compact ? 90 : 110 }}>
            <WatermarkSeal />
          </div>
        </div>

        {/* LEFT: Photo column */}
        <div
          className="flex flex-col items-center justify-center px-3 flex-shrink-0"
          style={{ zIndex: 1, borderRight: '1px solid #c8b86a40' }}
        >
          <div
            className="overflow-hidden flex items-center justify-center bg-gray-300"
            style={{
              width: photoW,
              height: photoH,
              border: '2px solid #b8860b',
              borderRadius: '2px',
            }}
          >
            {data.avatarUrl ? (
              <AppImage
                src={data.avatarUrl}
                alt={`Detective photo for ${data.displayName}`}
                width={photoW}
                height={photoH}
                className="w-full h-full object-cover"
              />
            ) : (
              <span style={{ fontSize: compact ? '28px' : '36px' }}>🕵🏾</span>
            )}
          </div>
          <p className="text-center text-[6px] font-mono text-gray-500 mt-1 tracking-widest">PHOTO ID</p>
        </div>

        {/* RIGHT: Info column */}
        <div className="flex flex-col flex-1 px-3 py-2 justify-between" style={{ zIndex: 1 }}>
          {/* Name & Title */}
          <div>
            <p
              className="font-bold text-gray-900 leading-none"
              style={{ fontSize: compact ? '12px' : '14px' }}
            >
              {data.displayName.split(' ')[0] || data.displayName}
            </p>
            <p
              className="font-semibold text-gray-700 leading-tight"
              style={{ fontSize: compact ? '9px' : '11px' }}
            >
              {data.displayName.split(' ').slice(1).join(' ') || data.detectiveAlias}
            </p>
            <div className="mt-1">
              <p className="text-[6px] font-mono uppercase tracking-widest text-gray-500">Title</p>
              <p className="text-[9px] font-bold text-gray-800 uppercase tracking-wide">{data.currentRank}</p>
            </div>
          </div>

          {/* Stats row */}
          <div className="flex gap-4">
            <div>
              <p className="text-[6px] font-mono uppercase tracking-widest text-gray-500">XP</p>
              <p className="text-[9px] font-bold text-gray-800 font-mono">{data.xpTotal.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-[6px] font-mono uppercase tracking-widest text-gray-500">Badges</p>
              <p className="text-[9px] font-bold text-gray-800 font-mono">{data.badges.length}</p>
            </div>
            <div>
              <p className="text-[6px] font-mono uppercase tracking-widest text-gray-500">Status</p>
              <p className="text-[9px] font-bold text-green-700 font-mono">ACTIVE</p>
            </div>
          </div>

          {/* Dates */}
          <div>
            <p className="text-[6px] font-mono uppercase tracking-widest text-gray-500">Issue / Exp</p>
            <p className="text-[8px] font-mono font-bold text-gray-800">{issueDate} / {expDate}</p>
          </div>

          {/* QR + Card serial */}
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[6px] font-mono uppercase tracking-widest text-gray-500 mb-0.5">Card Ser No.</p>
              <p
                className="font-mono font-bold text-gray-900"
                style={{ fontSize: compact ? '12px' : '14px', letterSpacing: '0.05em' }}
              >
                A&nbsp;{data.consultantId.replace(/\D/g, '').slice(-7)}
              </p>
              <p className="text-[5px] font-mono text-gray-400 tracking-widest leading-tight mt-0.5">
                LOSS OF THIS CARD MUST BE REPORTED IMMEDIATELY
              </p>
            </div>
            <div className="flex-shrink-0" style={{ border: '1px solid #b8860b', borderRadius: '2px', overflow: 'hidden' }}>
              <QRCodePlaceholder size={compact ? 32 : 40} />
            </div>
          </div>
        </div>
      </div>

      {/* ── BOTTOM BARCODE STRIP ── */}
      <div
        className="flex items-end justify-between px-3 py-1 flex-shrink-0"
        style={{ background: 'rgba(0,0,0,0.04)', borderTop: '1px solid #c8b86a' }}
      >
        <div className="flex items-end gap-px h-5">
          {BARCODE_HEIGHTS.map((h, i) => (
            <span
              key={`bbar-${i}`}
              style={{
                display: 'inline-block',
                width: i % 4 === 0 ? '2.5px' : i % 2 === 0 ? '1.5px' : '1px',
                height: `${Math.round(h * 0.55)}px`,
                background: '#1a1a1a',
                borderRadius: '0.5px',
              }}
            />
          ))}
        </div>
        <p className="text-[6px] font-mono text-gray-400 tracking-widest">SBPDA-{data.consultantId.replace(/\D/g,'').slice(-4)}</p>
      </div>
    </div>
  );
}